import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Power, MapPin, Navigation, Users, Briefcase, Minus, Plus, Clock, Car,
  Star, Loader2, Zap, ChevronRight, Hourglass, XCircle, CheckCircle2, Info, CalendarClock, Plane,
} from "lucide-react";
import { toast } from "sonner";
import { DriverShell } from "@/components/Shells";
import MapView from "@/components/MapView";
import Button from "@/components/Button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { primeSound, playRequestPing } from "@/lib/sound";
import { money, miles, fmtDateTime, cn } from "@/lib/format";
import { haversineMiles } from "@/lib/location";

function RequestPopup({ req, secsLeft, onSkip, onBid, driverLoc }) {
  const pickupEta = useMemo(() => {
    const d = haversineMiles(driverLoc, req.pickup);
    return d == null ? null : Math.max(2, Math.round(d / 0.5)); // ~30 mph city avg
  }, [driverLoc, req.pickup]);
  return (
    <div data-testid={`request-${req.id}`} className="absolute inset-x-4 bottom-4 z-20 rounded-3xl border border-border bg-card p-5 shadow-card animate-fade-up md:inset-x-auto md:right-6 md:w-[420px]">
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-1.5 rounded-full bg-brand-600 px-3 py-1 text-xs font-bold text-white">
          <Zap className="h-3.5 w-3.5" /> New ride request
        </span>
        <span className="flex items-center gap-1 text-sm font-bold text-foreground">
          <Clock className="h-4 w-4 text-muted-foreground" /> <span data-testid="popup-countdown">{secsLeft}s</span>
        </span>
      </div>
      <div className="mb-3 flex items-center gap-1.5 rounded-xl bg-accent px-3 py-2 text-sm font-bold text-brand-700 dark:text-brand-400" data-testid="request-when">
        <CalendarClock className="h-4 w-4" />
        {req.when === "scheduled" ? `Scheduled · ${fmtDateTime(req.scheduled_ts) || req.scheduled_time}` : "Pickup now"}
      </div>
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <Users className="h-4 w-4 text-brand-600" /> {req.passengers ?? 1} passenger{(req.passengers ?? 1) === 1 ? "" : "s"}
          <Star className="ml-1 h-3.5 w-3.5 fill-amber-400 text-amber-400" /> <span className="font-num text-muted-foreground">{req.customer_rating}</span>
        </span>
        <span className="font-num text-xl font-extrabold text-foreground">{money(req.recommended_fare)}</span>
      </div>
      <div className="flex gap-3 rounded-2xl bg-secondary p-3">
        <div className="flex flex-col items-center pt-1">
          <span className="h-2.5 w-2.5 rounded-full bg-success" />
          <span className="my-1 w-0.5 flex-1 bg-border" />
          <MapPin className="h-3.5 w-3.5 text-brand-600" />
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <p className="truncate text-sm font-medium text-foreground">{req.pickup.label}</p>
          <p className="truncate text-sm font-medium text-foreground">{req.destination.label}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {[
          ...(pickupEta != null ? [[Navigation, `${pickupEta} min to pickup`]] : []),
          [Clock, `${req.duration_min} min trip`],
          [MapPin, miles(req.distance_miles)],
          ...(req.airport_info?.airline ? [[Plane, req.airport_info.airline]] : []),
          ...(req.required_class_label ? [[Car, req.required_class_label]] : []),
          [Briefcase, `${req.bags ?? 0} bag${(req.bags ?? 0) === 1 ? "" : "s"}`],
        ].map(([Icon, label], i) => (
          <span key={i} className="flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-brand-700 dark:text-brand-400">
            <Icon className="h-3.5 w-3.5" /> {label}
          </span>
        ))}
      </div>
      <div className="mt-4 flex gap-3">
        <button onClick={onSkip} data-testid={`skip-${req.id}`} className="h-12 rounded-xl border-2 border-border px-6 text-sm font-semibold text-muted-foreground hover:border-brand-400">Skip</button>
        <button onClick={onBid} data-testid={`bid-${req.id}`} className="h-12 flex-1 rounded-xl bg-brand-600 text-sm font-semibold text-white hover:bg-brand-700">
          Set fare · {money(req.fare_min)}–{money(req.fare_max)}
        </button>
      </div>
    </div>
  );
}

export default function DriverDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [online, setOnline] = useState(false);
  const [approval, setApproval] = useState(user?.approval_status || "approved");
  const [requests, setRequests] = useState([]);
  const [active, setActive] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [driverLoc, setDriverLoc] = useState(null);
  const [queue, setQueue] = useState([]);
  const [secsLeft, setSecsLeft] = useState(25);
  const [holding, setHolding] = useState(false);
  const [selected, setSelected] = useState(null);
  const [bidFare, setBidFare] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);
  const seenReqRef = useRef(new Set());
  const loadedOnceRef = useRef(false);
  const current = holding ? null : queue[0] || null;

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  // Driver location (browser geolocation with IP fallback for iframe previews).
  useEffect(() => {
    let done = false;
    const apply = (lat, lng) => { if (!done) { done = true; setDriverLoc({ lat, lng }); } };
    const ipFallback = async () => {
      if (done) return;
      try {
        const r = await fetch("https://ipapi.co/json/");
        const j = await r.json();
        if (typeof j.latitude === "number") apply(j.latitude, j.longitude);
      } catch { /* noop */ }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => apply(p.coords.latitude, p.coords.longitude),
        () => ipFallback(),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
      setTimeout(ipFallback, 9000);
    } else ipFallback();
  }, []);

  const reqMarkers = useMemo(() => {
    if (online && requests.length) return requests.map((r) => ({ lat: r.pickup.lat, lng: r.pickup.lng }));
    if (!driverLoc) return [];
    const seeds = [[0.012, -0.018], [-0.02, 0.01], [0.022, 0.015], [-0.014, -0.02], [0.006, 0.026], [-0.026, 0.004]];
    return seeds.map(([dlat, dlng]) => ({ lat: driverLoc.lat + dlat, lng: driverLoc.lng + dlng }));
  }, [online, requests, driverLoc]);

  const poll = useCallback(async () => {
    try {
      const a = await api("/driver/active");
      setApproval(a.approval_status);
      setOnline(a.online);
      const driving = ["accepted", "driver_enroute", "arrived", "in_progress"];
      if (a.ride && driving.includes(a.ride.status)) {
        navigate(`/driver-trip/${a.ride.id}`);
        return;
      }
      setActive(a.ride && a.ride.status === "searching" ? a.ride : null);
      if (a.online && !(a.ride && a.ride.status === "searching")) {
        const r = await api("/driver/requests");
        setRequests(r.requests || []);
      } else {
        setRequests([]);
      }
    } catch { /* noop */ }
  }, [navigate]);

  useEffect(() => {
    poll();
    const iv = setInterval(poll, 3000);
    return () => clearInterval(iv);
  }, [poll]);

  // Queue newly-arrived requests as map popups + chime on genuinely new ones.
  useEffect(() => {
    if (!online) { setQueue([]); seenReqRef.current.clear(); loadedOnceRef.current = false; return; }
    const fresh = requests.filter((r) => !seenReqRef.current.has(r.id));
    if (fresh.length) {
      fresh.forEach((r) => seenReqRef.current.add(r.id));
      setQueue((q) => [...q, ...fresh]);
      if (loadedOnceRef.current) playRequestPing();
    }
    loadedOnceRef.current = true;
  }, [requests, online]);

  useEffect(() => {
    if (!current) return;
    setSecsLeft(25);
    const start = Date.now();
    const iv = setInterval(() => {
      const left = 25 - Math.floor((Date.now() - start) / 1000);
      if (left <= 0) { clearInterval(iv); setQueue((q) => q.slice(1)); }
      else setSecsLeft(left);
    }, 250);
    return () => clearInterval(iv);
  }, [current?.id]);

  const dismissCurrent = () => {
    setQueue((q) => q.slice(1));
    setHolding(true);
    setTimeout(() => setHolding(false), 3000);
  };

  const toggleOnline = async () => {
    if (approval !== "approved") {
      setNotice(approval === "declined"
        ? "Your driver application was declined. Please contact support."
        : "Your account is pending approval. You can go online once Getaride approves you.");
      return;
    }
    setToggling(true);
    try {
      const r = await api("/driver/online", { method: "POST", body: { status: online ? "offline" : "online" } });
      setOnline(r.online);
      if (r.online) { primeSound(); poll(); }
    } catch (e) {
      toast.error(e?.message || "Could not change status.");
    } finally { setToggling(false); }
  };

  const openBid = (req) => { setSelected(req); setBidFare(req.recommended_fare); };
  const adjust = (delta) => setBidFare((f) => {
    const v = Math.round((f + delta) * 2) / 2;
    return Math.min(selected.fare_max, Math.max(selected.fare_min, v));
  });

  const submitBid = async (fare) => {
    setSubmitting(true);
    try {
      const res = await api(`/rides/${selected.id}/bid`, { method: "POST", body: { fare } });
      const rideId = selected.id;
      setSelected(null);
      if (res?.offer_sent) {
        toast.success("Offer sent! We'll notify you when the rider picks a driver.");
        navigate("/driver/rides");
      } else {
        navigate(`/driver-trip/${rideId}`);
      }
    } catch (e) {
      toast.error(e?.message || "Could not submit offer.");
    } finally { setSubmitting(false); }
  };

  const notApproved = approval !== "approved";

  return (
    <DriverShell fullBleed>
      {notice && (
        <div data-testid="driver-notice" className="absolute inset-x-4 top-4 z-30 flex items-center gap-2 rounded-xl bg-accent px-4 py-3 text-sm font-medium text-brand-700 shadow-card dark:text-brand-400">
          <Info className="h-4 w-4 shrink-0" /> {notice}
        </div>
      )}

      {notApproved ? (
        <div className="h-full overflow-y-auto bg-secondary/40" data-testid="approval-banner">
          {/* Hero */}
          <div className="bg-card px-6 pb-10 pt-8 text-center">
            {approval === "declined" ? (
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
                <XCircle className="h-10 w-10 text-destructive" />
              </div>
            ) : (
              <img
                src="/driver-approval.png"
                alt="Getaride airport driver"
                className="mx-auto mb-6 h-48 w-full max-w-md rounded-3xl object-cover shadow-card"
              />
            )}
            <h1 className="text-3xl font-extrabold text-foreground">
              {approval === "declined" ? "Application not approved" : "Welcome to Getaride!"}
            </h1>
            <p className="mx-auto mt-2 max-w-sm text-base text-muted-foreground">
              {approval === "declined"
                ? "We couldn't approve your driver application at this time. Contact Getaride support and we'll be glad to help."
                : "Thanks for applying to drive. Our team is reviewing your details and we'll notify you the moment your account is approved."}
            </p>
          </div>

          {/* Progress tracker */}
          {approval !== "declined" && (
            <div className="px-5 py-8">
              <div className="mx-auto max-w-md rounded-2xl bg-secondary p-4">
                {["Account created", "Documents submitted", "Awaiting approval"].map((s, i) => (
                  <div key={s} className="flex items-center gap-2 py-1">
                    {i < 2 ? <CheckCircle2 className="h-5 w-5 text-success" />
                      : <Hourglass className="h-5 w-5 text-amber-600" />}
                    <span className="text-sm font-medium text-foreground">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : active ? (
        <div className="flex h-full flex-col">
          <button data-testid="active-banner" onClick={() => navigate(`/driver-trip/${active.id}`)} className="m-4 flex items-center gap-3 rounded-2xl bg-accent p-4 text-left">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-600"><Car className="h-5 w-5 text-white" /></span>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-foreground">Offer sent · {money(active.final_fare)}</p>
              <p className="truncate text-sm text-muted-foreground">{active.pickup.label} → {active.destination.label} · waiting for rider…</p>
            </div>
            <ChevronRight className="h-5 w-5 text-brand-600" />
          </button>
          <div className="relative flex-1"><MapView requestMarkers={reqMarkers} centerOn={driverLoc} showRoute={false} className="h-full" /></div>
        </div>
      ) : (
        <div className="relative h-full">
          <MapView requestMarkers={reqMarkers} centerOn={driverLoc} showRoute={false} className="h-full" />

          <div className="pointer-events-none absolute inset-x-0 top-4 z-10 flex justify-center">
            <span className="flex items-center gap-2 rounded-full bg-card px-4 py-2 text-sm font-semibold text-foreground shadow-card">
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-success" />
              {online ? `You're online · ${requests.length} nearby` : `${reqMarkers.length} ride requests near you`}
            </span>
          </div>

          {!online ? (
            <div className="absolute inset-x-4 bottom-4 z-20 rounded-3xl bg-card p-6 text-center shadow-card md:inset-x-auto md:left-1/2 md:w-[440px] md:-translate-x-1/2">
              <h2 className="text-xl font-extrabold text-foreground">You&apos;re offline</h2>
              <p className="mt-1 text-sm text-muted-foreground">Go online to start accepting these nearby ride requests.</p>
              <Button onClick={toggleOnline} loading={toggling} data-testid="online-toggle" className="mt-4 w-full">
                <Power className="h-5 w-5" /> Go online
              </Button>
            </div>
          ) : current ? (
            <RequestPopup req={current} secsLeft={secsLeft} onSkip={dismissCurrent} onBid={() => openBid(current)} driverLoc={driverLoc} />
          ) : (
            <div className="absolute inset-x-4 bottom-4 z-20 flex items-center justify-center gap-3 rounded-3xl bg-card p-5 shadow-card md:inset-x-auto md:left-1/2 md:w-[440px] md:-translate-x-1/2">
              <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
              <p className="text-sm font-medium text-muted-foreground">{holding && queue.length > 0 ? "Next request in a moment…" : "Waiting for ride requests near you…"}</p>
              <button onClick={toggleOnline} data-testid="online-toggle" className="ml-2 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-muted-foreground">Go offline</button>
            </div>
          )}
        </div>
      )}

      {/* Bid sheet */}
      {selected && (
        <div className="absolute inset-0 z-40 flex items-end justify-center bg-black/40" onClick={() => setSelected(null)}>
          <div data-testid="bid-sheet" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-t-3xl bg-card p-6 pb-8 shadow-card animate-fade-up sm:mb-6 sm:rounded-3xl">
            <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-secondary" />
            <h3 className="text-xl font-extrabold text-foreground">Submit your fare</h3>
            <p className="mb-5 mt-1 truncate text-sm text-muted-foreground">{selected.pickup.label} → {selected.destination.label}</p>
            <div className="mb-5 flex items-center justify-between">
              <button onClick={() => adjust(-0.5)} data-testid="fare-minus" className="flex h-14 w-14 items-center justify-center rounded-full bg-accent text-brand-600"><Minus className="h-6 w-6" /></button>
              <div className="text-center">
                <p className="font-num text-4xl font-extrabold text-foreground" data-testid="bid-fare">{money(bidFare)}</p>
                <p className="text-xs text-muted-foreground">Allowed {money(selected.fare_min)}–{money(selected.fare_max)}</p>
              </div>
              <button onClick={() => adjust(0.5)} data-testid="fare-plus" className="flex h-14 w-14 items-center justify-center rounded-full bg-accent text-brand-600"><Plus className="h-6 w-6" /></button>
            </div>
            <Button onClick={() => submitBid(bidFare)} loading={submitting} data-testid="submit-bid" className="w-full">Submit {money(bidFare)} bid</Button>
            <Button variant="secondary" onClick={() => submitBid(selected.recommended_fare)} data-testid="accept-recommended" className="mt-2 w-full">
              Accept recommended ({money(selected.recommended_fare)})
            </Button>
          </div>
        </div>
      )}
    </DriverShell>
  );
}
